/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Mikael Finstad <finstaden@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import IcalUtil from '../ical-util';
import IcalSlicer from '../slicer/ical-slicer';

/**
 * @private
 * @typedef {Record<string, *>} IcalApi TODO #555
 * @typedef {Record<string, *>} IcalApi.Component TODO #555
 * @typedef {Record<string, *>} IcalApi.Event TODO #555
 * @typedef {Record<string, *>} IcalApi.Event.occurrenceDetails TODO #555
 * @typedef {Record<string, *>} IcalApi.Time TODO #555
 */

// TODO Nebude fungovat dobře pro změněné instance opakování, které zasahují mimo rozsah opakované události.
// - Např. instance v budoucnosti posunutá do minulosti (do rozsahu hledání), se ve výsledcích neprojeví.
// - Propracovaný algoritmus by měl po dokončení iterace ještě projít i všechny
//   změněné instance a provést na nich isEventWithinRange.
// TODO Řazení výstupu
// - Ideální by bylo řazení dle DTSTART události a sekundárně dle jejího pořadí v kalendáři.
// - Nyní je seřazeno primárně dle pořadí, sekundárně dle pořadí v iterátoru.
// - Je otázka co s událostmi jejichž instance se posune
//   do minulosti / budoucnosti, u nich asi DTSTART sedět nebude.
/**
 * Rozbalovač kalendářů ve formátu iCalendar
 *
 * Poskytuje jednoduché API pro výběr událostí v zadaném časovém intervalu.
 * Původně vychází z knihovny `ical-expander`, která byla značně vylepšena.
 *
 * Výčet nejdůležitějších změn:
 * - Přejmenování metody between na expand.
 * - Přejmenování parametrů `after` / `before` na `startDate` / `endDate`.
 * - Obalení `startDate` a `endDate` na vstupu do nepovinného `opts` objektu.
 * - Zrušení zbytečných "alias" metod, ponechána jen hlavní metoda.
 * - Přesun všech proměnných vstupů do parametrů metody, aby se nemusela
 *   pro každý výpočet vytvářet nová instance.
 * - Vstupní a výstupní kalendář předáván jako {@link IcalApi.Component}
 *   a ne jen `string`.
 *   Předejde se tak zbytečným parsování a serializacím při sřetězení více
 *   podobných operací.
 * - Vstupy a interní reprezentace datumů změněna z JS Date na
 *   {@link IcalApi.Time}.
 *   Lze tak podporovat i výpočty s plovoucí časovou zónou atd.
 * - Použití {@link IcalApi.Time.toUnixTime} na porovnání datumů.
 *   Přesná a nejpřehlednější varianta, jak události porovnávat.
 * - Výstup sjednocen na pole {@link IcalApi.Event.occurrenceDetails}, aby se
 *   s ním lépe pracovalo, když API může iterovat i přes události bez opakování.
 * - Zrušení zbytečné funkcionality spojené s výjimkami z opakovaných událostí.
 *   Nyní vše řeší interně API pomocí {@link IcalApi.RecurExpansion}.
 * - [Odstraněn bug s nepřesným výběrem událostí.](https://github.com/mifi/ical-expander/issues/15)
 * - Odebrány informace o časových zónách, které zbytečně zvětšovaly výstup.
 * - Zrušeno zbytečné volitelné přeskočení navalidních datumů.
 *
 * Zdroje:
 * - [ical-expander](https://github.com/mifi/ical-expander)
 */
class IcalExpander {
  /**
   * @param {IcalApi} ICAL
   */
  constructor(ICAL) {
    this.slicer = new IcalSlicer(ICAL);
    this.util = new IcalUtil(ICAL);
  }

  /**
   * Vybere z kalendáře všechny výskyty událostí v zadaném časovém intervalu.
   * Stejně jako události je hledaný interval zleva uzavřený a zleva otevřený.
   * Libovolné ohraničující datum lze vynechat a pak z dané strany nebudou
   * události omezené.
   * Lze použít i pro vyhledání události v jeden konkrétní okamžik,
   * v tom případě se musí `startDate` a `endDate` nastavit na stejné datum.
   * @param {IcalApi.Component} calendar
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate]
   * @param {number} [opts.maxIterations]
   * @param {IcalApi.Time} [opts.startDate]
   * @returns {IcalApi.Event.occurrenceDetails}
   */
  expand(calendar, { endDate, maxIterations = Infinity, startDate } = {}) {
    const { util } = this;
    // Optimalizace výkonu rychlým ořezáním událostí do hledaného intervalu.
    // Pokud by se to neudělalo, bude mít vyhledávání v opakovaných událostech,
    // které začali v daleké minulosti špatný výkon.
    // Následující algoritmus by totiž byl nucený provést všechny iterace
    // mezi začátkem události a koncem hledaného intervalu, což může zabrat
    // mnoho času při tisících iterací (cca. 3 roky u denního opakování).
    // Po optimalizaci se iteruje mezi začátkem a koncem hledaného intervalu.
    const slicedCalendar = this.slicer.slice(calendar, { startDate, endDate });
    // Příprava pole s výsledkem.
    // Vzhledem k neznámému počtu iterací, které se budou procházet,
    // nelze použít funkcionální pístup.
    const occurrences = [];
    // Načtou a provážou se všechny události v ořezaném kalendáři
    // s lepším výkonem, než poskytuje API v "automatickém režimu".
    util.getAllEvents(slicedCalendar).forEach((event) => {
      // Vytvoří se iterátor, který již řeší výjimky z opakování.
      const iterator = event.iterator();
      let next;
      let i = 0;
      do {
        i += 1;
        next = iterator.next();
        if (next) {
          const occurrence = event.getOccurrenceDetails(next);
          const startTime = occurrence.startDate.toUnixTime();
          // We have passed the max date, stop
          if (endDate && startTime > endDate.toUnixTime()) break;
          // Check that we are within our range
          if (util.isEventWithinRange(occurrence, { endDate, startDate })) {
            occurrences.push(occurrence);
          }
        }
      } while (next && i < maxIterations);
    });
    return occurrences;
  }
}

export default IcalExpander;
